import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PageSpinner } from "@/shared/components/spinner";
import { useCaregiverPatients } from "@/portals/caregiver/context/CaregiverPatientsContext";
import { getMedications } from "@/features/patient/records/api/dataPipelineApi";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import { MedicationsSection } from "@/features/patient/medications/components/MedicationsView";
import { DiagnosisPage } from "@/portals/patient/pages/DiagnosisPage";
import { CareTreatmentPage } from "@/portals/patient/pages/CareTreatmentPage";
import { DiagnosticsPage } from "@/portals/patient/pages/DiagnosticsPage";
import { CompleteHistoryPage } from "@/portals/patient/pages/CompleteHistoryPage";
import type { Medication } from "@/shared/types";

const CATEGORY_LABELS: Record<string, string> = {
  Encounter: "Encounters",
  Condition: "Conditions",
  MedicationRequest: "Medications",
  AllergyIntolerance: "Allergies",
  Observation: "Observations",
  DiagnosticReport: "Diagnostic Reports",
  Procedure: "Procedures",
  Immunization: "Immunizations",
};

/**
 * Medications is the one category with a real backend-wired destination —
 * fetches this specific patient's real records (server-side category
 * enforcement already applied, see get_effective_patient_id_for_viewing),
 * rather than reusing MedicationsContext (which is scoped to the logged-in
 * caregiver's OWN records, not the patient being viewed).
 */
function CaregiverMedicationsSection({ patientId }: { patientId: string }) {
  const [medications, setMedications] = useState<Medication[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMedications(null);
    setError(null);
    getMedications(patientId)
      .then((records) => {
        if (cancelled) return;
        setMedications(records.map(adaptMedication));
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load medications. Please try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (error) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-[#F7F7F7] py-24 text-center">
        <p className="text-sm font-semibold text-[#F36F8F]">{error}</p>
      </div>
    );
  }
  if (medications === null) return <PageSpinner className="bg-[#F7F7F7]" />;
  return <MedicationsSection medications={medications} />;
}

// Every other category still runs on the same mock data the patient's own
// sidebar page shows today (see this session's scope decision: wire the
// navigation now, wire these pages to real per-patient data in a later
// pass). Several categories intentionally share one destination page where
// no dedicated one exists yet (Encounter/Procedure -> Care & Treatment;
// Observation/DiagnosticReport -> Diagnostics & Imaging;
// AllergyIntolerance/Immunization -> Complete History).
type Destination = "medications" | "diagnosis" | "care-treatment" | "diagnostics" | "complete-history";

const CATEGORY_DESTINATIONS: Record<string, Destination> = {
  MedicationRequest: "medications",
  Condition: "diagnosis",
  Encounter: "care-treatment",
  Procedure: "care-treatment",
  Observation: "diagnostics",
  DiagnosticReport: "diagnostics",
  AllergyIntolerance: "complete-history",
  Immunization: "complete-history",
};

/**
 * Reached by clicking a shared-category card on the caregiver dashboard (see
 * CaregiverDashboardView's SharedRecordCard) — one dynamic route for all 8
 * categories rather than 8 separate route definitions. "Back to shared
 * records" always returns to the dashboard root: activePatientId lives in
 * CaregiverPatientsContext (not local state), so it survives this
 * navigation and the same patient's card list reappears automatically.
 */
export function CaregiverCategoryRecordsPage() {
  const navigate = useNavigate();
  const { patientId, category } = useParams<{ patientId: string; category: string }>();
  const { patients, activePatientId, setActivePatientId, activePatientDetail, isDetailLoading } = useCaregiverPatients();
  // Keeps the context's cached detail aligned with whichever patient this
  // route is actually for — matters when this page is reached directly
  // (browser back/forward, a bookmarked/shared URL) rather than by clicking
  // through the dashboard, where the two would already agree.
  useEffect(() => {
    if (patientId && patientId !== activePatientId) setActivePatientId(patientId);
  }, [patientId, activePatientId, setActivePatientId]);

  const patient = patients.find((p) => p.id === patientId);
  const categoryLabel = category ? CATEGORY_LABELS[category] ?? category : "";
  const destination = category ? CATEGORY_DESTINATIONS[category] : undefined;

  // Always derived from the freshly-fetched detail, never from the patients
  // list alone — that list can be stale (e.g. access was just revoked by
  // the patient elsewhere) while this page is already open, and rendering
  // the category content underneath a header that still implies active
  // access is exactly what produced the "overlapping cards" bug: a live
  // 403 from the record fetch showing up alongside a shell that hadn't
  // caught up. Gating on this instead means the whole page agrees on one
  // source of truth.
  const detailMatchesRoute = activePatientDetail?.patientId === patientId;
  const hasCategoryAccess =
    detailMatchesRoute &&
    activePatientDetail!.accessStatus === "active" &&
    activePatientDetail!.sharedCategories.some((c) => c.category === category);

  return (
    <div className="min-h-full bg-[#F7F7F7]">
      <div className="flex flex-wrap items-center gap-4 px-4 pb-2 pt-4 lg:px-6 lg:pt-5">
        <PageBackButton onClick={() => navigate("/caregiver")} ariaLabel="Back to shared records" />
        <div>
          <p className="text-sm font-extrabold text-[#0A2947]">
            {patient ? `${patient.name}'s ${categoryLabel}` : categoryLabel}
          </p>
          <p className="flex items-center gap-1 text-xs text-[#6B7280]">
            <Icon icon="lucide:lock" className="h-3 w-3" /> Read-only access
          </p>
        </div>
      </div>

      {!detailMatchesRoute || isDetailLoading ? (
        <PageSpinner className="bg-[#F7F7F7]" />
      ) : !hasCategoryAccess ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 bg-[#F7F7F7] py-24 text-center">
          <Icon icon="lucide:folder-x" className="h-10 w-10 text-[#9CA3AF]" />
          <p className="text-sm font-semibold text-[#0A2947]">
            {activePatientDetail?.accessStatus === "revoked"
              ? "Your access to this patient has been revoked."
              : "You no longer have access to this category."}
          </p>
          <Button variant="outline" onClick={() => navigate("/caregiver")} className="!mt-2">
            Back to shared records
          </Button>
        </div>
      ) : (
        <>
          {patientId && destination === "medications" && <CaregiverMedicationsSection patientId={patientId} />}
          {destination === "diagnosis" && <DiagnosisPage />}
          {destination === "care-treatment" && <CareTreatmentPage />}
          {destination === "diagnostics" && <DiagnosticsPage />}
          {destination === "complete-history" && <CompleteHistoryPage />}
        </>
      )}
    </div>
  );
}
